import { type KeyboardEvent, type RefObject } from 'react';
import { Search, X } from 'lucide-react';

interface ChannelFilterInputProps {
  value: string;
  onChange: (value: string) => void;
  /** The input element, so a keyboard shortcut can focus it. */
  inputRef?: RefObject<HTMLInputElement | null>;
}

/**
 * The type-to-filter box above the chat list — narrows the loaded conversations
 * as you type (Telegram / Slack style). Escape clears it and gives focus back to
 * the page; the list itself offers "Search all chats" when nothing loaded
 * matches, since only the first page of conversations is in memory.
 */
export function ChannelFilterInput({ value, onChange, inputRef }: ChannelFilterInputProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onChange('');
      event.currentTarget.blur();
    }
  };

  return (
    <div className="relative px-3 py-2 border-b border-border">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"
      />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Filter conversations"
        aria-label="Filter conversations"
        autoComplete="off"
        className="w-full rounded-lg border border-border bg-background pl-7 pr-7 py-1.5 text-sm pointer-coarse:text-base outline-none focus:ring-2 focus:ring-primary/40 [&::-webkit-search-cancel-button]:hidden"
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear filter"
          className="absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
